import React from 'react';
import { Box, Flex, Menu, Text, UnstyledButton } from '@mantine/core';
import { IconArchive, IconChevronDown, IconEye, IconEyeOff } from '@tabler/icons-react';

// Pill next to the breadcrumbs showing whether the customer is active, hidden (inactive) or archived.
// For active and hidden customers it is also the control for hiding and showing them.

const PALETTE = {
  active: { bg: 'var(--mantine-color-green-0)', c: 'var(--mantine-color-green-9)' },
  hidden: { bg: 'var(--mantine-color-gray-2)', c: 'var(--mantine-color-gray-7)' },
  archived: { bg: 'var(--mantine-color-orange-0)', c: 'var(--mantine-color-orange-9)' },
};

const pillStyle = (status: keyof typeof PALETTE) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '2px 9px',
  borderRadius: 10,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: '18px',
  background: PALETTE[status].bg,
  color: PALETTE[status].c,
});

export default function CustomerStatusTag({ isActive, isArchived, disabled, onToggleActive }: {
  isActive: boolean;
  isArchived: boolean;
  disabled?: boolean;
  onToggleActive: () => void;
}) {
  if (isArchived) {
    return (
      <Box component={'span'} data-testid={'customer-status-tag'} style={pillStyle('archived')}>
        <IconArchive size={13} />Archived
      </Box>
    );
  }

  const status = isActive ? 'active' : 'hidden';
  return (
    <Menu position={'bottom-start'} offset={6} width={280} shadow={'md'} disabled={disabled}>
      <Menu.Target>
        <UnstyledButton data-testid={'customer-status-tag'} title={'Change status'} style={{ ...pillStyle(status), cursor: disabled ? 'default' : 'pointer' }}>
          <Box component={'span'} w={7} h={7} style={{ borderRadius: '50%', background: 'currentColor' }} />
          {isActive ? 'Active' : 'Hidden'}
          {!disabled && <IconChevronDown size={13} style={{ marginRight: -2 }} />}
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item onClick={onToggleActive}>
          <Flex gap={8} align={'flex-start'}>
            {isActive
              ? <IconEyeOff size={17} color={'var(--mantine-color-gray-6)'} style={{ marginTop: 1, flex: 'none' }} />
              : <IconEye size={17} color={'var(--mantine-color-gray-6)'} style={{ marginTop: 1, flex: 'none' }} />}
            <div>
              <Text size={'sm'} fw={500}>{isActive ? 'Hide this customer' : 'Show this customer'}</Text>
              <Text size={'xs'} c={'gray.6'} mt={2}>
                {isActive
                  ? 'This customer won\'t display for you or anyone on your team.'
                  : 'This customer will display again for you and your team.'}
              </Text>
            </div>
          </Flex>
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
